import type { CSSProperties } from "react";
import { Outlet } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { BottomNav, type NavItem } from "@/components/bottom-nav";
import { InstallCard, OfflineBanner } from "@/components/pwa";
import { workspaceQuery, useWs } from "@/lib/session";

export function loadWorkspace(queryClient: QueryClient, slug: string) {
  return queryClient.ensureQueryData(workspaceQuery(slug));
}

/** Text color on the primary color: black or white depending on brightness. */
function readableOn(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#111111" : "#ffffff";
}

export function WorkspaceLayout() {
  const { workspace, is_staff: isStaff, t, base } = useWs();
  const items: NavItem[] = isStaff
    ? [
        { href: base, label: t.navHome, icon: "home" },
        { href: `${base}/schedule`, label: t.navSchedule, icon: "calendar" },
        { href: `${base}/clients`, label: t.navClients, icon: "users" },
        { href: `${base}/series`, label: t.navLibrary, icon: "dumbbell" },
        { href: `${base}/settings`, label: t.navSettings, icon: "settings" },
      ]
    : [
        { href: base, label: t.navHome, icon: "home" },
        { href: `${base}/journal`, label: t.navHistory, icon: "history" },
        { href: `${base}/nutrition`, label: t.navNutrition, icon: "food" },
        { href: `${base}/me`, label: t.navProgress, icon: "chart" },
        { href: `${base}/settings`, label: t.navSettings, icon: "settings" },
      ];
  const style = {
    "--primary": workspace.primary_color,
    "--primary-fg": readableOn(workspace.primary_color),
  } as CSSProperties;
  return (
    <div style={style} className="min-h-dvh">
      <div className="mx-auto max-w-2xl px-4 pb-28 pt-[max(1rem,env(safe-area-inset-top))]">
        <OfflineBanner t={t} />
        <InstallCard t={t} dismissible />
        <Outlet />
      </div>
      <BottomNav items={items} />
    </div>
  );
}
